/**
 * Keyboard trigger for the diagnostic overlay.
 *
 * Two activation paths:
 *   - Ctrl+Shift+D (or Cmd+Shift+D on macOS) — immediate open.
 *   - Long-press Enter for 3 seconds — parity with the Flutter app's
 *     D-pad OK long-press. Works on TV remotes that map OK to Enter.
 *
 * The 3-second hold uses keydown with repeat guard (keydown fires
 * repeatedly while held) + keyup cancel. Short presses don't trigger.
 */

const LONG_PRESS_MS = 3000;

export interface KeyboardTriggerOptions {
  readonly onOpen: () => void;
  readonly isOverlayOpen: () => boolean;
}

export function installKeyboardTrigger(opts: KeyboardTriggerOptions): () => void {
  let pressTimer: ReturnType<typeof setTimeout> | null = null;

  const tryOpen = (): void => {
    if (opts.isOverlayOpen()) return;
    opts.onOpen();
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    // Ctrl/Cmd + Shift + D
    const isCombo = (e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'D' || e.key === 'd');
    if (isCombo) {
      e.preventDefault();
      tryOpen();
      return;
    }

    // Enter long-press
    if (e.key === 'Enter' && !e.repeat) {
      if (pressTimer !== null) return;
      pressTimer = setTimeout(() => {
        pressTimer = null;
        tryOpen();
      }, LONG_PRESS_MS);
    }
  };

  const onKeyUp = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      if (pressTimer !== null) {
        clearTimeout(pressTimer);
        pressTimer = null;
      }
    }
  };

  const onBlur = (): void => {
    if (pressTimer !== null) {
      clearTimeout(pressTimer);
      pressTimer = null;
    }
  };

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onBlur);

  return () => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('blur', onBlur);
    if (pressTimer !== null) clearTimeout(pressTimer);
  };
}
